import { useState, useEffect } from "react";
import { Search, Bell, BellRing, Activity, Menu, ChevronRight, LogOut, Volume2, VolumeX, Focus } from "lucide-react";
import { useNav } from "@/contexts/NavContext";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useServiceAlerts } from "@/hooks/useServiceAlerts";
import GlobalSearch from "@/components/GlobalSearch";

const SEGMENT_LABELS = {
    "dashboard": "Dashboard", "dispatch": "Despacho", "map": "Mapa",
    "services": "Servicios", "new": "Nuevo", "quoter": "Cotizador", "quotes": "Cotizaciones",
    "clients": "Clientes", "agreements": "Convenios", "price-lists": "Listas de Precios",
    "profitability": "Rentabilidad", "units": "Unidades", "personnel": "Personal",
    "attendance": "Asistencia", "maintenance": "Mantenimiento", "inventory": "Inventario",
    "receivable": "Por Cobrar", "payable": "Por Pagar", "aging": "Antigüedad",
    "sla": "SLA", "expirations": "Alertas", "incidents": "Incidentes",
    "reports": "Reportes", "crew": "Móvil Tripulación", "audit": "Auditoría",
    "settings": "Configuración", "integrations": "Integraciones",
    "portal-users": "Usuarios Portal", "hospital-contacts": "Hospitales",
    "statement": "Estado de Cuenta", "frap": "FRAP",
};

export default function Topbar({ title, subtitle }) {
    const [now, setNow] = useState(new Date());
    const [searchOpen, setSearchOpen] = useState(false);
    const { setMobileOpen } = useNav();
    const location = useLocation();
    const { user, logout } = useAuth();
    const isAmbulancia = user?.role === "ambulancia";
    const { alerts, unreadCount, markAllRead, enableAudio, audioEnabled } = useServiceAlerts(user);
    const [alertsPanelOpen, setAlertsPanelOpen] = useState(false);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Atajo Ctrl/Cmd + K
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const segments = location.pathname.split("/").filter(Boolean);

    return (
        <>
            <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 md:px-6 justify-between flex-shrink-0 gap-3" data-testid="topbar">
                <div className="flex items-center gap-3 min-w-0">
                    {!isAmbulancia && (
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="md:hidden p-1.5 hover:bg-slate-100 rounded-sm"
                            data-testid="mobile-menu-btn"
                            title="Abrir menú"
                        >
                            <Menu className="w-5 h-5 text-slate-700" />
                        </button>
                    )}
                    <div className="min-w-0">
                        <h1 className="font-display font-bold text-base tracking-tight text-slate-900 truncate">{title}</h1>
                        <div className="hidden sm:flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wider">
                            {segments.length > 0 ? (
                                <>
                                    <Link to={isAmbulancia ? "/crew" : "/dashboard"} className="hover:text-red-600">Inicio</Link>
                                    {segments.map((seg, i) => {
                                        const path = "/" + segments.slice(0, i + 1).join("/");
                                        const isLast = i === segments.length - 1;
                                        const label = SEGMENT_LABELS[seg] || seg.slice(0, 8);
                                        return (
                                            <span key={path} className="flex items-center gap-1">
                                                <ChevronRight className="w-3 h-3" />
                                                {isLast ? <span className="text-slate-700 font-semibold">{label}</span> : <Link to={path} className="hover:text-red-600">{label}</Link>}
                                            </span>
                                        );
                                    })}
                                </>
                            ) : subtitle}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                    {!isAmbulancia && (
                        <button
                            onClick={() => setSearchOpen(true)}
                            className="hidden lg:flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-sm w-72 text-left hover:border-red-300 transition-colors group"
                            data-testid="global-search-trigger"
                        >
                            <Search className="w-3.5 h-3.5 text-slate-400 group-hover:text-red-600" strokeWidth={1.5} />
                            <span className="text-sm text-slate-400 flex-1">Buscar folio, cliente, unidad…</span>
                            <kbd className="text-[10px] font-mono border border-slate-300 rounded-sm px-1.5 py-0.5 text-slate-500 bg-slate-50">⌘K</kbd>
                        </button>
                    )}
                    {!isAmbulancia && (
                        <button
                            onClick={() => setSearchOpen(true)}
                            className="lg:hidden p-1.5 hover:bg-slate-100 rounded-sm"
                            data-testid="global-search-trigger-mobile"
                            title="Buscar"
                        >
                            <Search className="w-4 h-4 text-slate-700" strokeWidth={1.5} />
                        </button>
                    )}
                    <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-sm">
                        <Activity className="w-3 h-3 text-emerald-600 fm-pulse text-emerald-500" strokeWidth={2} />
                        <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">En Vivo</span>
                    </div>
                    <div className="font-mono text-xs text-slate-700 tabular-nums hidden xl:block">
                        {now.toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </div>
                    {/* Toggle de audio para alertas */}
                    <button
                        onClick={enableAudio}
                        className={`p-2 rounded-sm relative transition-colors ${audioEnabled ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-500 hover:bg-slate-100"}`}
                        data-testid="audio-alerts-toggle"
                        title={audioEnabled ? "Audio de alertas activado · click para probar" : "🔇 Click para activar sonido de alertas (requerido una vez por sesión)"}
                    >
                        {audioEnabled ? <Volume2 className="w-4 h-4" strokeWidth={1.5} /> : <VolumeX className="w-4 h-4" strokeWidth={1.5} />}
                    </button>
                    {/* Modo Concentración (cabina full-screen) */}
                    {!isAmbulancia && (
                        <Link
                            to="/focus"
                            className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-900 bg-slate-900 hover:bg-red-600 hover:border-red-600 text-white rounded-sm text-[10px] font-bold uppercase tracking-widest transition-colors"
                            data-testid="focus-mode-btn"
                            title="Modo Concentración · pantalla completa de cabina"
                        >
                            <Focus className="w-3.5 h-3.5" strokeWidth={2} /> Concentración
                        </Link>
                    )}
                    {/* Bell con badge de alertas */}
                    <div className="relative">
                        <button
                            onClick={() => { setAlertsPanelOpen(!alertsPanelOpen); markAllRead(); }}
                            className="relative p-2 hover:bg-slate-100 rounded-sm"
                            data-testid="notifications-btn"
                            title={unreadCount > 0 ? `${unreadCount} alerta(s) nueva(s)` : "Sin alertas nuevas"}
                        >
                            {unreadCount > 0
                                ? <BellRing className="w-4 h-4 text-red-600 animate-pulse" strokeWidth={1.8} />
                                : <Bell className="w-4 h-4 text-slate-700" strokeWidth={1.5} />}
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-600 text-white text-[9px] font-bold rounded-full">
                                    {unreadCount > 9 ? "9+" : unreadCount}
                                </span>
                            )}
                        </button>
                        {alertsPanelOpen && (
                            <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-sm shadow-xl z-50" data-testid="alerts-panel">
                                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                                    <div className="font-bold text-xs uppercase tracking-widest">Alertas recientes</div>
                                    {!audioEnabled && (
                                        <button onClick={enableAudio} className="text-[10px] text-emerald-700 hover:underline" data-testid="enable-audio-link">Activar sonido</button>
                                    )}
                                </div>
                                <div className="max-h-72 overflow-y-auto fm-scroll">
                                    {alerts.length === 0 ? (
                                        <div className="px-3 py-6 text-center text-xs text-slate-400 italic">Sin alertas. Las nuevas solicitudes / asignaciones aparecerán aquí en tiempo real.</div>
                                    ) : (
                                        alerts.map((a) => (
                                            <Link key={a.id} to={`/services/${a.id}`} onClick={() => setAlertsPanelOpen(false)} className="block px-3 py-2 border-b border-slate-100 hover:bg-red-50 last:border-b-0">
                                                <div className="font-mono text-[11px] font-bold text-red-700">{a.folio}</div>
                                                <div className="text-xs text-slate-700 truncate">{a.client_name}</div>
                                                <div className="text-[10px] text-slate-400 uppercase tracking-widest">{a.status?.replace(/_/g, " ")} · {new Date(a.at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</div>
                                            </Link>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {isAmbulancia && (
                        <button
                            onClick={logout}
                            className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 hover:border-red-400 hover:bg-red-50 text-slate-700 rounded-sm text-xs font-bold uppercase tracking-wider"
                            data-testid="ambulancia-logout-btn"
                            title="Cerrar sesión de unidad"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Salir
                        </button>
                    )}
                </div>
            </header>
            {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
        </>
    );
}
